import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  ALLOWED_IMAGE_MIME_TYPES,
  MAX_IMAGE_BYTES,
  type AllowedImageMime,
} from "./upload-validation";

/**
 * Server-side verification of a freshly uploaded object in the `card-images`
 * bucket. Confirms the caller owns the path (path prefix == userId), the
 * stored mime type is allowed, and the byte size is within the 10MB limit.
 * If anything is off, the object is deleted with the service role and the
 * function throws so the client aborts the listing.
 */
export const verifyCardImageUpload = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { path: string }) => {
    if (!input || typeof input.path !== "string" || !input.path) {
      throw new Error("path required");
    }
    if (input.path.includes("..") || input.path.startsWith("/")) {
      throw new Error("invalid path");
    }
    return { path: input.path };
  })
  .handler(async ({ data, context }) => {
    const { path } = data;
    const { userId } = context;

    // Enforce owner-scoped folder prefix.
    const [ownerFolder] = path.split("/");
    if (ownerFolder !== userId) {
      throw new Error("Forbidden: path does not belong to caller");
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Look up the object's stored metadata.
    const slash = path.lastIndexOf("/");
    const dir = slash >= 0 ? path.slice(0, slash) : "";
    const name = slash >= 0 ? path.slice(slash + 1) : path;

    const { data: entries, error: listErr } = await supabaseAdmin.storage
      .from("card-images")
      .list(dir, { search: name, limit: 1 });
    if (listErr) throw listErr;

    const entry = entries?.find((e) => e.name === name);
    if (!entry) throw new Error("Uploaded file not found");

    const meta = (entry.metadata ?? {}) as {
      size?: number;
      mimetype?: string;
    };
    const size = typeof meta.size === "number" ? meta.size : 0;
    const mime = (meta.mimetype ?? "").toLowerCase() as AllowedImageMime;

    const invalid =
      size <= 0 ||
      size > MAX_IMAGE_BYTES ||
      !ALLOWED_IMAGE_MIME_TYPES.includes(mime);

    if (invalid) {
      await supabaseAdmin.storage.from("card-images").remove([path]);
      if (size > MAX_IMAGE_BYTES) throw new Error("File exceeds 10MB limit");
      throw new Error("Only JPG, PNG and WEBP images are allowed");
    }

    // Magic-byte (file signature) check. Content-Type is attacker-controlled;
    // read the first bytes of the stored object and confirm they match a real
    // JPEG / PNG / WEBP header before accepting the upload.
    const { data: blob, error: dlErr } = await supabaseAdmin.storage
      .from("card-images")
      .download(path);
    if (dlErr || !blob) {
      await supabaseAdmin.storage.from("card-images").remove([path]);
      throw new Error("Could not verify uploaded file");
    }
    const head = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
    const detected = detectImageSignature(head);

    if (!detected || detected !== mime) {
      await supabaseAdmin.storage.from("card-images").remove([path]);
      throw new Error("Only real JPG, PNG and WEBP images are allowed");
    }

    return { ok: true as const };
  });

/**
 * Inspect the first bytes of a file and return the mime type implied by its
 * magic-byte signature, or null if it doesn't match an allowed image format.
 * References:
 *   JPEG:  FF D8 FF
 *   PNG:   89 50 4E 47 0D 0A 1A 0A
 *   WEBP:  "RIFF" .... "WEBP"  (bytes 0-3 = 52 49 46 46, bytes 8-11 = 57 45 42 50)
 */
function detectImageSignature(b: Uint8Array): AllowedImageMime | null {
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) {
    return "image/jpeg";
  }
  if (
    b.length >= 8 &&
    b[0] === 0x89 &&
    b[1] === 0x50 &&
    b[2] === 0x4e &&
    b[3] === 0x47 &&
    b[4] === 0x0d &&
    b[5] === 0x0a &&
    b[6] === 0x1a &&
    b[7] === 0x0a
  ) {
    return "image/png";
  }
  if (
    b.length >= 12 &&
    b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 &&
    b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50
  ) {
    return "image/webp";
  }
  return null;
}
